import { useAtom } from 'jotai'
import { atomWithStorage, RESET } from 'jotai/utils'
import { useCallback, useMemo } from 'react'
import { StorageKeys } from '~/atoms/StorageKeys'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { PARKING_LOT_PREFIX_DIGITS } from '~/pages/WebPhone/utils/constants'
import { replacer, reviver } from '~/utils/maps'

import { ContactSourceType, ExtensionPreview } from '@mangovoice/types'

export type RecentCall = {
  contact: ContactDetails
  callTime: Date
  numberOfCalls: number
}

const recentCallsAtom = atomWithStorage<RecentCall[]>(
  StorageKeys.RecentCallsList,
  [],
)
const recentCallMapAtom = atomWithStorage<string>(
  StorageKeys.RecentCallMap,
  JSON.stringify(new Map<number, RecentCall[]>(), replacer),
)

export const useRecentCalls = (
  pbxId: number | undefined,
  pbxExtensions: ExtensionPreview[],
) => {
  const [legacyRecentCalls, setLegacyRecentCalls] = useAtom(recentCallsAtom)
  const [recentCallMapEncoded, setRecentCallMapEncoded] =
    useAtom(recentCallMapAtom)

  const recentCallMap = useMemo(() => {
    return new Map<number, RecentCall[]>(
      JSON.parse(recentCallMapEncoded, reviver),
    )
  }, [recentCallMapEncoded])
  const setRecentCallMap = useCallback(
    (newMap: Map<number, RecentCall[]>) => {
      setRecentCallMapEncoded(JSON.stringify(newMap, replacer))
    },
    [setRecentCallMapEncoded],
  )

  // TODO: Remove this migration code after a few releases
  if (legacyRecentCalls.length > 0 && pbxId !== undefined) {
    // Migrate data
    const recentCallMap = new Map<number, RecentCall[]>()
    recentCallMap.set(pbxId, legacyRecentCalls)
    setRecentCallMap(recentCallMap)
    // The RESET keyword deletes the key from storage
    setLegacyRecentCalls(RESET)
  }

  const { recentCalls, recentCallsLength } = useMemo(() => {
    if (pbxId === undefined) {
      return { recentCalls: [], recentCallsLength: 0 }
    }
    const calls = recentCallMap.get(pbxId)
    return { recentCalls: calls ?? [], recentCallsLength: calls?.length ?? 0 }
  }, [recentCallMap, pbxId])
  const setRecentCalls = useCallback(
    (calls: RecentCall[]) => {
      if (pbxId === undefined) {
        return
      }
      const newMap = new Map<number, RecentCall[]>(recentCallMap)
      newMap.set(pbxId, calls)
      setRecentCallMap(newMap)
    },
    [setRecentCallMap, pbxId],
  )

  function sortByCallTime(a: RecentCall, b: RecentCall) {
    return new Date(b.callTime).getTime() - new Date(a.callTime).getTime()
  }

  const upsertRecentCall = useCallback(
    (
      callee:
        | string
        | { contactNumber: ContactNumber; contact: ContactDetails },
    ) => {
      const callTime = new Date()

      let number: string
      let contact: ContactDetails | undefined = undefined
      if (typeof callee === 'string') {
        number = callee
      } else {
        number = callee.contactNumber.number
        contact = callee.contact
        contact.id = callee.contact.id === -1 ? 0 : callee.contact.id
      }

      // Don't log calls to parking lots
      if (number.length === 4 && number.startsWith(PARKING_LOT_PREFIX_DIGITS)) {
        return
      }

      const existingCall = recentCalls.find(
        (call) => call.contact.defaultNumber?.number === number,
      )

      if (existingCall) {
        // Update in place
        if (contact !== undefined && typeof callee !== 'string') {
          existingCall.contact = contact
          existingCall.contact.defaultNumber = callee.contactNumber
        }
        setRecentCalls(
          recentCalls
            .map((call) => {
              if (call.contact.defaultNumber?.number === number) {
                return {
                  contact: existingCall.contact,
                  callTime,
                  numberOfCalls: call.numberOfCalls + 1,
                }
              }
              return call
            })
            .sort(sortByCallTime),
        )
      } else {
        // Add a new call, keep only top 10
        const sortedCalls = recentCalls.sort(sortByCallTime)

        let contact: ContactDetails
        let isExtension: boolean = false
        let name: string = ''
        if (typeof callee === 'string') {
          if (number.length <= 5) {
            isExtension = true
            const extension = pbxExtensions.find((ext) => ext.number === number)
            name = extension?.name ?? ''
          }
          contact = {
            id: 0,
            name: name,
            defaultNumber: {
              id: 0,
              number,
              label: 'work',
              isPrimary: false,
              isExtension,
              availableActions: [],
              availableQuickActions: [],
            },
            numbers: [],
            source: isExtension
              ? ContactSourceType.Extensions
              : ContactSourceType.Unknown,
            sourceName: 'Recent Calls',
          }
        } else {
          isExtension = callee.contactNumber.isExtension ?? false
          contact = callee.contact
          contact.defaultNumber = callee.contactNumber
        }

        sortedCalls.unshift({
          contact,
          callTime,
          numberOfCalls: 1,
        })
        const topTen = sortedCalls.slice(0, 10)
        setRecentCalls(topTen)
      }
    },
    [recentCallsLength, pbxExtensions],
  )

  const recentContacts = useMemo(() => {
    return recentCalls.sort(sortByCallTime).map((call) => call.contact)
  }, [recentCallsLength])

  if (pbxId === undefined) {
    return { recentCalls: [], recentContacts: [], upsertRecentCall: () => {} }
  }
  return { recentCalls, recentContacts, upsertRecentCall }
}
